import { Target, Briefcase, CalendarClock, AlertTriangle } from "lucide-react";
import { Reveal, ChapterTag } from "@/components/Reveal";

const SERVICES = [
  {
    number: "01",
    icon: Target,
    title: "5 PH Strategy",
    description:
      "A structured options-trading strategy focused on disciplined execution and high-probability setups.",
    testId: "card-5-ph-strategy",
  },
  {
    number: "02",
    icon: Briefcase,
    title: "Portfolio + CCS Strategy",
    description:
      "A combined approach to long-term portfolio building and defined-risk options trading.",
    testId: "card-portfolio-ccs-strategy",
  },
  {
    number: "03",
    icon: CalendarClock,
    title: "Weekly Strategies",
    description:
      "Practical weekly options strategies designed for structured risk management and consistent execution.",
    testId: "card-weekly-strategies",
  },
];

export default function Offerings() {
  return (
    <section
      id="offerings"
      data-testid="offerings-section"
      className="scroll-mt-20 bg-[#F3EDE2]"
    >
      <div className="mx-auto max-w-6xl px-4 py-20 sm:px-6 sm:py-28 lg:px-8 lg:py-36">
        <Reveal>
          <ChapterTag number="02" label="What I Offer" />
          <h2 className="mt-4 max-w-2xl font-display text-2xl font-bold leading-snug tracking-tight text-[#1C1B1A] sm:text-3xl lg:text-4xl">
            Three structured ways to trade with discipline
          </h2>
        </Reveal>

        <div className="mt-14 grid grid-cols-1 gap-6 md:grid-cols-3">
          {SERVICES.map((s, i) => (
            <Reveal key={s.number} delay={i * 0.12}>
              <article
                data-testid={s.testId}
                className="group flex h-full flex-col rounded-2xl border border-[#E2D9CC] bg-white p-7 transition-all duration-300 hover:-translate-y-1.5 hover:border-[#E05A10]/60 hover:shadow-[0_24px_50px_-20px_rgba(224,90,16,0.25)] sm:p-8"
              >
                <div className="flex items-center justify-between">
                  <span className="font-mono text-xs tracking-[0.25em] text-[#8C857B]">
                    {s.number}
                  </span>
                  <s.icon
                    size={22}
                    className="text-[#E05A10] transition-transform duration-300 group-hover:scale-110"
                  />
                </div>
                <h3 className="mt-8 font-display text-xl font-semibold tracking-tight text-[#1C1B1A] sm:text-2xl">
                  {s.title}
                </h3>
                <p className="mt-3 text-sm leading-relaxed text-[#524E48] sm:text-base">
                  {s.description}
                </p>
                <div className="mt-auto pt-8">
                  <div className="h-px w-full bg-[#EFE8DC] transition-colors duration-300 group-hover:bg-[#E05A10]/50" />
                </div>
              </article>
            </Reveal>
          ))}
        </div>

        <Reveal delay={0.1}>
          <p
            data-testid="offers-pop-note"
            className="mt-12 max-w-3xl text-sm leading-relaxed text-[#524E48] sm:text-base"
          >
            <span className="font-semibold text-[#1C1B1A]">Please note:</span>{" "}
            these strategies are designed around setups with a Probability of
            Profit (PoP) above 90%, but returns are{" "}
            <span className="font-semibold text-[#B43B00]">not guaranteed</span>.
          </p>
        </Reveal>

        <Reveal delay={0.15}>
          <div
            data-testid="offers-risk-disclaimer-box"
            className="mt-6 flex gap-4 rounded-2xl border border-[#F3C89B] bg-[#FFF8F0] p-6 sm:p-8"
          >
            <AlertTriangle size={22} className="mt-0.5 shrink-0 text-[#E05A10]" />
            <div>
              <p className="font-mono text-xs font-medium uppercase tracking-[0.2em] text-[#B43B00]">
                Risk Disclaimer
              </p>
              <p className="mt-2 text-xs leading-relaxed text-[#524E48] sm:text-sm">
                Stock market and options trading involve risk. All content and
                training provided by SAATH Trainings are for educational
                purposes only. A high probability of profit does not assure
                returns, and past performance does not guarantee future results.
              </p>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
}
